import { ArrowRight, BedDouble, Calendar, Car, CheckCircle2, Compass, MapPin, Sparkles, Users, UtensilsCrossed } from "lucide-react";
import { API } from "@/api";
import { GoldImage } from "../GoldImage";
import { eur, isActivity, stopCity } from "./utils";

const CountRow = ({ icon: Icon, label, count, id }) => (
    <div className="flex items-center justify-between py-1.5 text-xs">
        <span className="flex items-center gap-2 text-stone-400"><Icon size={12} className="text-amber-500/70" />{label}</span>
        <span className="font-mono-accent font-semibold text-amber-200" data-testid={`ts-count-${id}`}>{count}</span>
    </div>
);

/* Riepilogo viaggio sempre accessibile: durata, destinazioni, viaggiatori,
 * servizi selezionati, totale indicativo SOLO da prezzi reali noti, CTA
 * «Richiedi preventivo personalizzato» collegata al flusso esistente. */
export const TripSummary = ({ proposal, trip, onQuote, doneReq, tc, lang, compact = false }) => {
    const stops = proposal?.stops || [];
    const nights = stops.reduce((a, s) => a + (Number(s.nights) || 0), 0);
    const stays = trip?.stays || [];
    const exps = trip?.experiences || [];
    const transfers = trip?.transfers || [];
    const acts = exps.filter(isActivity).length;
    const known = [...stays, ...exps, ...transfers].filter((x) => x.price != null);
    const total = known.reduce((a, x) => a + Number(x.price), 0);

    return (
        <aside className={`rounded-2xl border border-amber-500/25 bg-[#0b0b0c]/90 p-5 ${compact ? "" : "sticky top-24 self-start"}`} data-testid="trip-summary">
            <div className="relative h-28 overflow-hidden rounded-xl">
                <GoldImage src={`${API}/public/site-media/dest-roma.jpg`} alt="Roma" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <p className="absolute bottom-2.5 left-3 text-[10px] uppercase tracking-[0.25em] text-amber-300 font-semibold">{tc.summaryTitle}</p>
            </div>

            <div className="mt-4 space-y-0.5 divide-y divide-amber-500/5">
                <div className="flex items-center justify-between py-1.5 text-xs">
                    <span className="flex items-center gap-2 text-stone-400"><Calendar size={12} className="text-amber-500/70" />{tc.duration}</span>
                    <span className="font-semibold text-amber-100" data-testid="ts-duration">{nights > 0 ? `${nights} ${tc.nights} · ${nights + 1} ${tc.days}` : "—"}</span>
                </div>
                <div className="flex items-center justify-between gap-3 py-1.5 text-xs">
                    <span className="flex shrink-0 items-center gap-2 text-stone-400"><MapPin size={12} className="text-amber-500/70" />{tc.destinations}</span>
                    <span className="truncate text-right font-semibold text-amber-100" data-testid="ts-destinations">{stops.length ? stops.map((s) => stopCity(s, lang)).join(" · ") : "—"}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 text-xs">
                    <span className="flex items-center gap-2 text-stone-400"><Users size={12} className="text-amber-500/70" />{tc.travelers}</span>
                    <span className="font-semibold text-amber-100" data-testid="ts-travelers">{proposal?.participants || "—"}</span>
                </div>
            </div>

            <div className="mt-2 border-t border-amber-500/10 pt-2">
                <CountRow icon={BedDouble} label={tc.sumStays} count={stays.length} id="stays" />
                <CountRow icon={Compass} label={tc.sumExperiences} count={exps.length - acts} id="experiences" />
                <CountRow icon={Car} label={tc.sumTransfers} count={transfers.length} id="transfers" />
                <CountRow icon={Sparkles} label={tc.sumActivities} count={acts} id="activities" />
                <CountRow icon={UtensilsCrossed} label={tc.sumRestaurants} count={(trip?.restaurants || []).length} id="restaurants" />
            </div>

            <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-400/5 p-3.5 text-center">
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500/80">{tc.totalLabel}</p>
                <p className="mt-1 font-display text-xl text-amber-200" data-testid="ts-total">{total > 0 ? eur(total) : tc.totalUnknown}</p>
                {total > 0 && <p className="mt-0.5 text-[9px] text-stone-500">{tc.totalKnownNote}</p>}
            </div>

            {doneReq ? (
                <div className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-4 text-center" data-testid="concierge-req-success">
                    <CheckCircle2 size={20} className="mx-auto text-emerald-300" />
                    <p className="mt-1.5 text-xs text-stone-300">{tc.successMsg}</p>
                    <p className="mt-2 font-mono-accent text-base font-bold text-amber-300" data-testid="concierge-req-code">{doneReq.code}</p>
                </div>
            ) : (
                <button onClick={onQuote} disabled={!proposal} data-testid="concierge-request-open"
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(241,211,108,0.35)] disabled:opacity-40">
                    {tc.quoteCta} <ArrowRight size={13} />
                </button>
            )}
            <p className="mt-3 text-center text-[9px] leading-relaxed text-stone-600">{tc.disclaimer}</p>
        </aside>
    );
};
